import React, { useState, useRef, useEffect } from 'react'
import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid'

export interface AudioPlayerProps {
  /** Full URL to the audio file (including any query params like ?signature=...) */
  src: string
  /** Whether the player should loop. Defaults to true. */
  loop?: boolean
  /** Whether to pause & reset when the player scrolls out of view. Defaults to true. */
  pauseOnHide?: boolean
  /** Extra class name for the container */
  className?: string
}

/**
 * Performant audio player that updates the progress bar via direct DOM
 * manipulation (no React re-renders during playback).
 *
 * Features:
 * - Play / Pause with reset-to-start on pause
 * - Click-to-seek on the progress bar
 * - Optional loop (default: on)
 * - Auto-pause when scrolled out of the viewport (IntersectionObserver)
 */
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  loop = true,
  pauseOnHide = true,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const rafRef = useRef<number>(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const setProgressWidth = (ratio: number) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${ratio * 100}%`
    }
  }

  // Smooth 60 fps progress — no state updates
  useEffect(() => {
    const tick = () => {
      const audio = audioRef.current
      if (audio && audio.duration && !audio.paused) {
        setProgressWidth(audio.currentTime / audio.duration)
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick)
    }
    return () => cancelAnimationFrame(rafRef.current)
  }, [isPlaying])

  // Auto-pause when scrolled out of view
  useEffect(() => {
    if (!pauseOnHide) return
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause()
          audioRef.current.currentTime = 0
          setIsPlaying(false)
          setProgressWidth(0)
        }
      },
      { threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [pauseOnHide])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      audio.currentTime = 0
      setIsPlaying(false)
      setProgressWidth(0)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !audio.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * audio.duration
    setProgressWidth(ratio)
  }

  return (
    <div ref={containerRef} className={`flex items-center gap-3 ${className}`}>
      <audio ref={audioRef} src={src} preload="none" loop={loop} />
      <button
        onClick={togglePlay}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        {isPlaying ? (
          <PauseIcon className="h-4 w-4 text-gray-600" />
        ) : (
          <PlayIcon className="h-4 w-4 text-gray-600 ml-0.5" />
        )}
      </button>
      <div
        className="flex-1 h-1.5 rounded-full bg-gray-200 cursor-pointer"
        onClick={seek}
      >
        <div
          ref={progressBarRef}
          className="h-full rounded-full bg-gray-500"
          style={{ width: '0%' }}
        />
      </div>
    </div>
  )
}
